import { useEffect, useRef, useState, useCallback } from "react";

interface UsePollingOptions<T = any> {
  pollFn: () => Promise<T> | T;
  interval?: number;
  enabled?: boolean;
  immediate?: boolean;
}

export const usePolling = <T = any>({
  pollFn,
  interval = 5000,
  enabled = true,
  immediate = false,
}: UsePollingOptions<T>) => {
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollFnRef = useRef(pollFn);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<T | null>(null);

  // Keep pollFnRef up to date with the latest pollFn
  useEffect(() => {
    pollFnRef.current = pollFn;
  }, [pollFn]);

  const executePoll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await pollFnRef.current();
      setData(result);
      setLoading(false);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setLoading(false);
      console.error("Error in polling function:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Execute immediately if requested
    if (immediate) {
      executePoll();
    }

    // Start polling
    pollingRef.current = setInterval(executePoll, interval);

    // Cleanup on unmount or dependency change
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [executePoll, interval, enabled, immediate]);

  return {
    data,
    loading,
    error,
    refetch: executePoll,
  };
};

